import { Fragment, useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Download, Loader2, Search, ChevronDown, ChevronRight, Send, X } from "lucide-react";

const STATUS = {
  queued: ["Em fila", "bg-zinc-100 text-zinc-700"], sending: ["A enviar", "bg-zinc-100 text-zinc-700"],
  sent: ["Enviado", "bg-sky-50 text-sky-800"], delivered: ["Entregue", "bg-emerald-50 text-emerald-800"],
  read: ["Lido", "bg-emerald-100 text-emerald-900"], failed: ["Falhou", "bg-red-50 text-red-700"],
  blocked: ["Bloqueado", "bg-amber-50 text-amber-800"], skipped: ["Ignorado", "bg-zinc-100 text-zinc-500"],
};
const ORIGIN_CLS = { manual: "text-indigo-700", auto: "text-emerald-700", unknown: "text-zinc-400" };
const EMPTY = { org_id: "", event_id: "", promoter_q: "", kind: "", msg_status: "", origin_type: "", date_from: "", date_to: "" };
const PAGE = 50;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "—");
const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v));

function Sel({ value, onChange, children, testid }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="form-input text-sm">{children}</select>;
}

function Filters({ f, set, orgs, events, kinds, onApply, onReset }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="msglog-filters">
      <Sel value={f.org_id} onChange={(v) => set({ ...f, org_id: v, event_id: "" })} testid="msglog-filter-org">
        <option value="">Todas as agências</option>
        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </Sel>
      <Sel value={f.event_id} onChange={(v) => set({ ...f, event_id: v })} testid="msglog-filter-event">
        <option value="">{f.org_id ? "Todos os eventos" : "Evento (escolhe a agência)"}</option>
        {events.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.date}</option>)}
      </Sel>
      <div className="relative">
        <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={f.promoter_q} onChange={(e) => set({ ...f, promoter_q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && onApply()}
          placeholder="Promotor (nome ou telefone)" style={{ paddingLeft: "2.25rem" }} className="form-input text-sm w-full" data-testid="msglog-filter-promoter" />
      </div>
      <Sel value={f.kind} onChange={(v) => set({ ...f, kind: v })} testid="msglog-filter-kind">
        <option value="">Todos os tipos</option>
        {kinds.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
      </Sel>
      <Sel value={f.msg_status} onChange={(v) => set({ ...f, msg_status: v })} testid="msglog-filter-status">
        <option value="">Todos os estados</option>
        {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
      </Sel>
      <Sel value={f.origin_type} onChange={(v) => set({ ...f, origin_type: v })} testid="msglog-filter-origin">
        <option value="">Todas as origens</option>
        <option value="manual">Manual (gestor)</option>
        <option value="auto">Automático</option>
        <option value="unknown">Origem desconhecida</option>
      </Sel>
      <input type="date" value={f.date_from} onChange={(e) => set({ ...f, date_from: e.target.value })} className="form-input text-sm" data-testid="msglog-filter-from" title="Desde" />
      <input type="date" value={f.date_to} onChange={(e) => set({ ...f, date_to: e.target.value })} className="form-input text-sm" data-testid="msglog-filter-to" title="Até" />
      <div className="col-span-2 md:col-span-4 flex gap-2 justify-end">
        <button onClick={onReset} data-testid="msglog-reset" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50"><X className="w-4 h-4" /> Limpar</button>
        <button onClick={onApply} data-testid="msglog-apply" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold"><Search className="w-4 h-4" /> Filtrar</button>
      </div>
    </div>
  );
}

function Detail({ r }) {
  return (
    <tr className="bg-zinc-50/70" data-testid={`msglog-detail-${r.id}`}>
      <td colSpan={8} className="px-5 py-4 text-xs text-zinc-700 space-y-2">
        <div className="grid sm:grid-cols-3 gap-2">
          <div><span className="text-zinc-400">Telefone:</span> {r.recipient_phone || "—"}</div>
          <div><span className="text-zinc-400">Enviado:</span> {fmt(r.sent_at)} · <span className="text-zinc-400">Atualizado:</span> {fmt(r.updated_at)}</div>
          <div className="truncate"><span className="text-zinc-400">WAMID:</span> {r.external_message_id || "—"}</div>
        </div>
        {r.origin?.inferred && <div className="text-amber-700">Origem deduzida (envio anterior ao registo de origem).</div>}
        {r.origin?.triggered_by_name && <div>Execução manual do processo automático por <strong>{r.origin.triggered_by_name}</strong>.</div>}
        {r.template_params?.length > 0 && <div><span className="text-zinc-400">Variáveis do template:</span> {r.template_params.join(" · ")}</div>}
        {(r.error_code || r.error_message) && <div className="text-red-700" data-testid={`msglog-error-${r.id}`}>Erro {r.error_code}: {r.error_message}</div>}
        <pre className="whitespace-pre-wrap font-sans bg-white border border-zinc-200 rounded-lg p-3 text-zinc-800">{r.body_preview || "(sem texto)"}</pre>
      </td>
    </tr>
  );
}

function Row({ r, open, onToggle }) {
  const [sl, sc] = STATUS[r.status] || [r.status, "bg-zinc-100 text-zinc-700"];
  return (
    <Fragment>
      <tr onClick={onToggle} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`msglog-row-${r.id}`}>
        <td className="px-3 py-2.5 text-zinc-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
        <td className="px-3 py-2.5 whitespace-nowrap tabular-nums text-zinc-600">{fmt(r.created_at)}</td>
        <td className="px-3 py-2.5">{r.organization_name || "—"}</td>
        <td className="px-3 py-2.5"><div className="font-medium text-zinc-900">{r.promoter_name || r.recipient_phone || "—"}</div><div className="text-xs text-zinc-500">{r.event_name ? `${r.event_name} · ${r.event_date}` : ""}</div></td>
        <td className="px-3 py-2.5">{r.kind_label}</td>
        <td className="px-3 py-2.5 font-mono text-xs text-zinc-600">{r.template_name || "texto livre"}</td>
        <td className="px-3 py-2.5"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sc}`}>{sl}</span></td>
        <td className={`px-3 py-2.5 text-xs font-medium ${ORIGIN_CLS[r.origin?.type] || ""}`} data-testid={`msglog-origin-${r.id}`}>{r.origin?.label}</td>
      </tr>
      {open && <Detail r={r} />}
    </Fragment>
  );
}

export default function SuperadminMessageLog() {
  const [f, setF] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [orgs, setOrgs] = useState([]);
  const [events, setEvents] = useState([]);
  const [data, setData] = useState({ total: 0, items: [], kinds: [] });
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { api.get("/admin/organizations").then((r) => setOrgs(r.data || [])); }, []);
  useEffect(() => {
    if (!f.org_id) { setEvents([]); return; }
    api.get("/admin/message-log/events", { params: { org_id: f.org_id } }).then((r) => setEvents(r.data || []));
  }, [f.org_id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/message-log", { params: { ...clean(applied), skip, limit: PAGE } });
      setData(r.data);
    } finally { setLoading(false); }
  }, [applied, skip]);
  useEffect(() => { load(); }, [load]);

  const apply = () => { setSkip(0); setApplied(f); };
  const reset = () => { setF(EMPTY); setSkip(0); setApplied(EMPTY); };
  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/message-log/export.csv", { params: clean(applied), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `registo-envios-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  return (
    <div className="space-y-5 min-w-0 max-w-full overflow-hidden" data-testid="superadmin-message-log">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950 flex items-center gap-3"><Send className="w-7 h-7" /> Registo de envios</h1>
          <p className="text-sm text-zinc-500 mt-1">Todas as mensagens WhatsApp enviadas pelas agências, com a origem de cada envio.</p>
        </div>
        <button onClick={exportCsv} disabled={exporting} data-testid="msglog-export-csv" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-zinc-200 bg-white text-sm font-semibold hover:bg-zinc-50 disabled:opacity-50">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Exportar CSV
        </button>
      </div>
      <Filters f={f} set={setF} orgs={orgs} events={events} kinds={data.kinds || []} onApply={apply} onReset={reset} />
      <div className="bg-white border border-zinc-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="w-8" /><th className="px-3 py-2.5 text-left">Data</th><th className="px-3 py-2.5 text-left">Agência</th>
              <th className="px-3 py-2.5 text-left">Promotor · Evento</th><th className="px-3 py-2.5 text-left">Tipo</th>
              <th className="px-3 py-2.5 text-left">Template Meta</th><th className="px-3 py-2.5 text-left">Estado</th><th className="px-3 py-2.5 text-left">Origem</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => <Row key={r.id} r={r} open={openId === r.id} onToggle={() => setOpenId(openId === r.id ? null : r.id)} />)}
            {!loading && data.items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-400" data-testid="msglog-empty">Sem envios para estes filtros.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm text-zinc-600" data-testid="msglog-pagination">
        <span data-testid="msglog-total">{loading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `${data.total} envio(s)`}</span>
        <div className="flex gap-2">
          <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))} data-testid="msglog-prev" className="px-3 py-1.5 rounded-lg border border-zinc-200 disabled:opacity-40">Anterior</button>
          <button disabled={skip + PAGE >= data.total} onClick={() => setSkip(skip + PAGE)} data-testid="msglog-next" className="px-3 py-1.5 rounded-lg border border-zinc-200 disabled:opacity-40">Seguinte</button>
        </div>
      </div>
    </div>
  );
}
